import { call, type AccountPage } from "@baton/api";
import { keys, LIST_LIMIT, normaliseQuery, type CursorPageParam, type Runtime } from "@baton/core";
import { infiniteQueryOptions } from "@tanstack/react-query";

/**
 * 020 `contracts/http-api.md` § Accounts: the client list's read under its filters, a page at a time,
 * and the filters themselves. The Clients sidebar reads the list from the shell's first load, so the
 * read is a module of its own: taken from `../queries.ts`, every client read and write of the
 * Clients screens came into that load with it (FR-015-120, 2026-10-05). It calls its route through
 * `call` rather than `@baton/api`'s `accounts` operations, whose module holds every Clients
 * operation and would come into the first load whole for this one route. The other reads and writes
 * stay in `../queries.ts`, keyed from the same scheme.
 */
export interface AccountFilters {
  readonly status: string | null;
  readonly stage: string | null;
  readonly type: string | null;
  readonly country: string | null;
  /** An M49 area: the clients located by it alone, their country not known (020 FR-020-262). */
  readonly region: string | null;
  /** An account group's id: the clients that belong to it. */
  readonly account_group: string | null;
  readonly q: string;
  /** An RFC 3339 instant: the clients last touched after it (020 FR-020-302). */
  readonly last_interaction_after: string | null;
  /** An RFC 3339 instant: the clients last touched before it; never beside `stale_days`, which the list refuses. */
  readonly last_interaction_before: string | null;
  /** Whole days, 1 to 3,650, as the URL writes them: the clients last touched longer ago than that, or never. */
  readonly stale_days: string | null;
}

export const NO_ACCOUNT_FILTERS: AccountFilters = {
  status: null,
  stage: null,
  type: null,
  country: null,
  region: null,
  account_group: null,
  q: "",
  last_interaction_after: null,
  last_interaction_before: null,
  stale_days: null,
};

/**
 * The client list's orders, the list request's `order_by` values (020 `contracts/http-api.md`
 * § Accounts): by name, the default, by when the client was created, by when it entered its milestone,
 * by country, and by the last interaction, newest first with the clients never touched last
 * (FR-020-302). The list's heads sort by name and by the last interaction; the other three arrive
 * with a client list view whose `display.order_by` names one (FR-020-303), so the list keeps them all.
 */
export const ACCOUNT_ORDERS = ["name", "created_at", "stage_entered_at", "country", "last_interaction"] as const;
export type AccountOrder = (typeof ACCOUNT_ORDERS)[number];

/** The filters as the list's own parameters, an unset one absent. */
export function paramsOf(filters: Readonly<Record<string, string | null>>): Readonly<Record<string, string>> {
  return Object.fromEntries(
    Object.entries(filters).flatMap(([name, value]) =>
      value === null || value.trim().length === 0 ? [] : [[name, value.trim()]],
    ),
  );
}

/** The filters as the list request's query members: the set ones by their own names, `stale_days` as the number it is. */
export function listQuery(filters: AccountFilters) {
  const { stale_days: staleDays, ...rest } = paramsOf({ ...filters });
  return { ...rest, ...(staleDays === undefined ? {} : { stale_days: Number(staleDays) }) };
}

export const nextCursor = (last: { readonly next_cursor: string | null }): CursorPageParam | undefined =>
  last.next_cursor === null ? undefined : { cursor: last.next_cursor };

/**
 * The client list under `filters`, a page at a time. `openTasks` asks each client for its open tasks
 * as well (020 `contracts/http-api.md` § Accounts, the list's `open_tasks`, 2026-09-28), which only
 * the client board draws: its read is its own entry under the board's key, which a task's write
 * re-reads (`@baton/core` `dashboardActions`), and the list's and the Clients sidebar's reads stay the
 * lean one.
 */
export function accountsQuery(runtime: Runtime, ws: string, filters: AccountFilters, openTasks = false, summary = false, order: AccountOrder = "name") {
  const params = paramsOf({ ...filters });
  // The default order is the request's own when it names none, so it is left out of the request and the key alike; the board keeps its columns' own order.
  const ordered = order === "name" || openTasks ? {} : { order_by: order };
  return infiniteQueryOptions({
    queryKey: openTasks ? keys.accountBoard(ws, normaliseQuery(params)) : summary ? keys.accountSummary(ws, normaliseQuery({ ...params, ...ordered })) : keys.accountsList(ws, normaliseQuery({ ...params, ...ordered })),
    initialPageParam: { cursor: null },
    getNextPageParam: nextCursor,
    queryFn: async ({ pageParam, signal }): Promise<AccountPage> =>
      (
        await call(runtime.signedIn, "get", "/api/v1/workspaces/{ws}/accounts", {
          params: { ws },
          query: {
            limit: LIST_LIMIT,
            ...listQuery(filters),
            ...ordered,
            ...(openTasks ? { open_tasks: true } : {}),
            // 020 FR-020-280: where each client stands, for the list's status columns.
            ...(summary ? { summary: true } : {}),
            ...(pageParam.cursor === null ? {} : { cursor: pageParam.cursor }),
          },
          signal,
        })
      ).data,
  });
}
